/**
 * ★★ EVT-04 — L'ÉCRAN QUI N'EXISTE PAS POUR CE TENANT-LÀ.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * POURQUOI UN RENVOI, ET NON UNE PHRASE
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Quatre écrans du produit ont pour SUJET l'abonnement : la vente d'une formule
 * dans la boutique, la reprise d'un fichier d'abonnés, le déplacement d'une
 * série en régie, la consultation de l'espace d'un abonné. Un tenant de type
 * `evenement` n'en a aucun — « pas de saison, pas de formule, pas de campagne »
 * (EVT-02) — et son provisionnement ne crée ni formule ni souscription.
 *
 * Il y avait donc trois façons de traiter ces quatre écrans, et une seule est
 * honnête :
 *
 *  · **les réécrire** — impossible sans mentir : on ne raconte pas « choisir
 *    l'abonné dont on déplace la série » à quelqu'un qui n'a ni l'un ni
 *    l'autre. Le vocabulaire n'est pas le problème, le sujet l'est ;
 *  · **y afficher un vide poli** (« cet outil ne vous concerne pas ») — c'est
 *    un écran de plus à traverser pour rien, et SPEC-BOUTIQUE §8 ter D fait du
 *    libre-service intégral une condition de rentabilité : chaque impasse est
 *    un appel au support qui détruit la marge d'un forfait à CHF 50.– ;
 *  · **renvoyer là où quelque chose existe** — c'est ce que fait ce composant.
 *
 * ★ EVT-04 dit « les écrans de campagne, de formules et de réabonnement
 * **n'apparaissent pas** ». Les retirer du menu (`navigation.ts`,
 * `masqueSiEvenement`) suffit pour ceux qui EN SONT une entrée ; les quatre
 * ci-dessus n'en sont pas — on y arrive par un onglet, par une adresse gardée
 * en signet, ou par le bouton « précédent ». Ce composant ferme ce chemin-là.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * CE QU'IL NE FAIT PAS, ET C'EST VOLONTAIRE
 * ────────────────────────────────────────────────────────────────────────────
 *
 * **Il ne remplace aucun mot.** Le vocabulaire accordé au tenant est un autre
 * problème, et il vit dans `lib/vocabulaire.ts`. Mélanger les deux produirait
 * la triche que le garde-fou de rendu cherche justement : un écran vert parce
 * qu'il s'est tu.
 *
 * **Il n'est pas une garde d'autorisation.** L'organisateur est administrateur
 * chez lui et l'API ne lui refuse rien ; ces routes répondraient, vides. C'est
 * du confort d'interface, exactement comme `RequireStaff` côté rôles.
 *
 * `replace` : l'adresse écartée ne doit pas rester dans l'historique, sinon le
 * bouton « précédent » y ramène et la boucle se referme.
 */

import type { ReactElement } from 'react'
import { Navigate } from 'react-router'

import { useTheatre } from '../lib/theatre'
import { estEvenement } from '../lib/vocabulaire'

export interface EcranSansObjetProps {
  /** Où renvoyer : un écran qui, lui, a un sens pour un événement ponctuel. */
  readonly vers: string
  /** L'écran servi à tous les autres tenants — inchangé. */
  readonly children: ReactElement
}

export function EcranSansObjet({ vers, children }: EcranSansObjetProps): ReactElement {
  /* `client` tant que la requête est en vol : on n'écarte jamais un écran par
     accident de réseau. Voir `lib/theatre.ts`, même règle de sens. */
  const { type } = useTheatre()
  if (estEvenement(type)) return <Navigate to={vers} replace />
  return children
}
